import type { Crumb } from '@/types'
import type { Faq, SiteImage } from '@/types/content'

import { BUSINESS, EXTERIOR_ONLY, SITE_URL } from '@/content/business'
import { COUNTIES, TOWNS } from '@/content/towns'

type Json = Record<string, unknown>

export const BUSINESS_ID = `${SITE_URL}/#business`
export const WEBSITE_ID = `${SITE_URL}/#website`

export const absoluteUrl = (path: string): string => {
    if (/^https?:\/\//.test(path)) return path

    return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

export const areaServedSchema = (): Json[] => [
    ...TOWNS.map(town => ({
        '@type': 'City',
        name: `${town.name}, Alabama`,
        containedInPlace: { '@type': 'AdministrativeArea', name: `${town.county}, Alabama` }
    })),
    ...COUNTIES.map(county => ({ '@type': 'AdministrativeArea', name: `${county}, Alabama` }))
]

export const businessSchema = (): Json => ({
    '@context': 'https://schema.org',
    '@type': ['HomeAndConstructionBusiness', 'RoofingContractor'],
    '@id': BUSINESS_ID,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    alternateName: ['ACM Pros', 'ACM Pros Exteriors', 'The ACM Pros Exteriors'],
    url: `${SITE_URL}/`,
    logo: absoluteUrl('/images/acm/logo.webp'),
    image: absoluteUrl('/images/acm/na.webp'),
    telephone: BUSINESS.phoneE164,
    description: `Family-owned exterior contractor based in ${BUSINESS.base}, serving Huntsville and North Alabama since ${BUSINESS.founded}. ${EXTERIOR_ONLY.statement} Services: exterior window replacement, exterior door installation, siding, roofing, and pressure and soft washing.`,
    foundingDate: String(BUSINESS.founded),
    slogan: BUSINESS.footerTagline,
    knowsAbout: [
        'Exterior window replacement',
        'Exterior door installation',
        'Storm windows',
        'Storm doors',
        'Fiber cement siding',
        'Soffit and fascia',
        'Roof replacement',
        'Roof ventilation',
        'Soft washing',
        'Pressure washing'
    ],
    address: {
        '@type': 'PostalAddress',
        addressLocality: BUSINESS.locality,
        addressRegion: BUSINESS.region,
        addressCountry: BUSINESS.country
    },
    areaServed: areaServedSchema(),
    openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '07:00',
        closes: '19:00'
    }],
    identifier: {
        '@type': 'PropertyValue',
        name: 'Alabama contractor license',
        value: BUSINESS.license
    },
    founder: [
        { '@type': 'Person', name: 'Brad Miller' },
        { '@type': 'Person', name: 'Chase Miller' }
    ],
    sameAs: [BUSINESS.facebookUrl, BUSINESS.instagramUrl, BUSINESS.googleReviewsUrl],
    hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Exterior home services',
        itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Exterior window replacement', url: absoluteUrl('/services/window-replacement/') } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Exterior door installation', url: absoluteUrl('/services/door-installation/') } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Siding', url: absoluteUrl('/services/siding/') } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Roofing', url: absoluteUrl('/services/roofing/') } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pressure and soft washing', url: absoluteUrl('/services/pressure-washing/') } }
        ]
    }
})

export const websiteSchema = (): Json => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: BUSINESS.name,
    publisher: { '@id': BUSINESS_ID },
    inLanguage: 'en-US',
    potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/search/?q={search_term_string}` },
        'query-input': 'required name=search_term_string'
    }
})

export const breadcrumbSchema = (crumbs: Crumb[], currentPath: string): Json => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        item: absoluteUrl(crumb.href ?? currentPath)
    }))
})

export const faqSchema = (faqs: Faq[]): Json | null => {
    if (!faqs.length) return null

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.answer }
        }))
    }
}

interface ServiceSchemaInput {
    name: string
    description: string
    path: string
    serviceType?: string
    areaName?: string
    image?: string
}

export const serviceSchema = ({ name, description, path, serviceType, areaName, image }: ServiceSchemaInput): Json => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    serviceType: serviceType ?? name,
    description,
    url: absoluteUrl(path),
    provider: { '@id': BUSINESS_ID },
    areaServed: areaName ? { '@type': 'Place', name: areaName } : areaServedSchema(),
    ...(image ? { image: absoluteUrl(image) } : {})
})

export const imageSchema = (images: SiteImage[]): Json[] => images.map(image => ({
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: absoluteUrl(image.src),
    caption: image.caption ?? image.alt,
    ...(image.label === 'place' && image.caption ? { contentLocation: { '@type': 'Place', name: image.caption } } : {}),
    ...(image.credit ? { creditText: image.credit } : {})
}))

interface ArticleSchemaInput {
    title: string
    description: string
    path: string
    image?: string | null
    published: string
    modified: string
}

export const articleSchema = ({ title, description, path, image, published, modified }: ArticleSchemaInput): Json => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    url: absoluteUrl(path),
    mainEntityOfPage: absoluteUrl(path),
    datePublished: published,
    dateModified: modified,
    inLanguage: 'en-US',
    author: { '@type': 'Organization', name: `${BUSINESS.name} team`, url: `${SITE_URL}/about/` },
    publisher: { '@id': BUSINESS_ID },
    ...(image ? { image: absoluteUrl(image) } : {})
})
